import * as turf from '@turf/turf';
import Promise from 'bluebird';
import _ from 'lodash';
import tinycolor from 'tinycolor2';
import * as topojson from 'topojson-client';
import api from '../../../api';
import { notifyError } from '../../../shared';
import {
    createSymbologyScale,
    filterFeatures,
    getCurrentBounds,
    getShapeCentroid,
    isValidNumber
} from './VectorLayerUtils';
import { ZoomLevel } from './ZoomLevel';
import environment from '../../../environment';
import { NOTIFY_TYPE, notify } from '../../../shared/notify';
import translate from '../../../core/tools/translate';
import { LocaleTypeEnum } from '../../../../enuns/localeType.enum';

const notifyOutOfScale = _.throttle(
    () => notify(NOTIFY_TYPE.LOG, translate('Message.WarningSmallerAreaToViewData')),
    5000,
    { leading: true, trailing: false }
);

// import 'mapbox.js';

class VectorLayer extends L.Class {
    constructor(options = {}) {
        super();

        const defaults = {
            remote: {
                // url: undefined,
                method: 'GET'
                // params: undefined,
            },
            // map: undefined,
            // above: false,
            // autofocus: false,
            // autoUpdate: false,
            // autoUpdateInterval: undefined,
            // cluster: false,
            // intensity: undefined,
            controlRange: 35,
            // dynamic: false,
            // filter: undefined,
            // label: undefined,
            // popup: undefined,
            // scaleRange: [0,10],
            // showAll: true,
            // symbology: undefined,
            // uniqueField: 'id',
            visibleAtScale: true
            // value: 'value',
        };

        this.options = _.assign({}, defaults, options);

        if (options.map) {
            this.setMap(options.map);
        }

        this._fireClickEvent = this._fireClickEvent.bind(this);
        this._fireMouseoutEvent = this._fireMouseoutEvent.bind(this);
        this._fireMouseoverEvent = this._fireMouseoverEvent.bind(this);
        this._getColorRange = this._getColorRange.bind(this);
        this._getStyle = this._getStyle.bind(this);
        this._idleListener = this._idleListener.bind(this);
        this._makeRequest = this._makeRequest.bind(this);
        this._removeClusterPolygon = this._removeClusterPolygon.bind(this);
        this._zoomChangeListener = this._zoomChangeListener.bind(this);
        this.closePopup = this.closePopup.bind(this);
        this.highlight = this.highlight.bind(this);
        this.unhighlight = this.unhighlight.bind(this);

        this._init();
    }

    static author() {
        return {
            author: `Helmuth Saatkamp <helmuthdu@gmail.com>`,
            company: 'Neocom'
        };
    }

    setMap(map) {
        if (map) {
            this._map = map;
        }
    }

    // Get the map (if any) that the layer has been added to
    getMap() {
        return this._map;
    }

    setOptions(options = {}) {
        const remote = prop => (options.remote || {})[prop] || (this.options.remote || {})[prop] || null;
        if (options.remote) {
            options.remote = {
                url: remote('url'),
                method: remote('method') || 'GET',
                params: remote('params')
            };
        }

        if (options.map) {
            this.setMap(options.map);
        }

        this.options = _.assign({}, this.options, options);
    }

    redraw() {
        delete this.state.position;
        this._clear();
        return this._show();
    }

    visible() {
        return this.state.visible;
        // return this._map.hasLayer(this.layer);
    }

    isEmpty() {
        return !this.state.features.size;
    }

    async reload() {
        if (!this._eventListenerAdded) {
            this._addEventListener();
        }

        if (this._checkLayerZoomVisibility()) {
            if (this.options.autoUpdate && this.options.autoUpdateInterval) {
                this._autoUpdateInterval = setInterval(this._makeRequest, this.options.autoUpdateInterval);
            }

            if (this.options.showAll) {
                this._gotAll = true;
            }

            const featureCollection = this.toGeoJSON();
            if (this.options.reload) {
                const fc = await this.options.reload(featureCollection);
                this._processFeatures(fc || featureCollection);
                return;
            }

            this._processFeatures(featureCollection);
        }
    }

    reset() {
        this.clear();
        this.redraw();
    }

    addLayer(feature, reload = false) {
        if (feature) {
            this.state.features.set(this._getFeatureId(feature), feature);
            if (reload) {
                this.reload();
            }
        } else {
            this._makeRequest();
        }
    }

    removeLayer(id, reload = true) {
        try {
            this.state.features.delete(String(id));
        } catch (err) {
            console.log('WARNING: ', err);
        }
        if (reload) {
            this.reload();
        }
    }

    clear(force = false) {
        if (force) {
            this._clear();
        } else {
            this._hideVectors();
        }
        this._setInitialState();
        delete this.layer;
    }

    clearFeatures() {
        this.state.features = new Map();
    }

    getFeatures() {
        return _.orderBy([...this.state.features.values()], v => v.properties[this.options.sort]);
    }

    toggleIncrementalFeatures() {
        const { incremental } = this.options;
        this.setOptions({ incremental: !incremental });
    }

    async setFeatures(featureCollection, reload = false) {
        if (!featureCollection) {
            this.state.features = [];
            await this.reload();
            return null;
        }

        if (featureCollection.type === 'Topology' && featureCollection.objects && featureCollection.objects.output) {
            // Convert to GeoJSON
            featureCollection = topojson.feature(featureCollection, featureCollection.objects.output);
        }

        if (!_.isArray((featureCollection || {}).features)) {
            return null;
        }

        // Create an array to hold the features
        if (!this.options.incremental) {
            this.state.features = new Map();
        }

        _.forEach(featureCollection.features, f => {
            this.state.features.set(this._getFeatureId(f), f);
        });

        // Method to set in "Feature Collection".
        this.getCurrentGeoJSON();

        if (reload) {
            await this.reload();
        }
    }

    getCurrentFeatures() {
        if (this.options.filter) {
            if (this.state.featureCollection && _.size(this.state.featureCollection.features)) {
                return this.state.featureCollection.features;
            }
            return filterFeatures(this.getFeatures(), {
                filter: this.options.filter,
                bounds: this.options.expandView ? this.getMap().getBounds().pad(this.options.expandView) : this.getMap().getBounds().pad(0.1),
            }).features;
        }
        return this.getFeatures();
    }

    getType() {
        return this.options.type || 'default';
    }

    checkType(value) {
        if (value) {
            if (_.isArray(value)) {
                return _.some(value, t => t === this.getType());
            } else {
                return this.getType() === value;
            }
        } else {
            return this.options.type;
        }
    }

    toGeoJSON(features = this.getFeatures()) {
        if (!features.length) {
            return { features: [] };
        }

        return turf.featureCollection(
            _(features)
                .orderBy(v => v.properties[this.options.sort])
                .value()
        );
    }

    getCurrentGeoJSON() {
        const featureCollection = filterFeatures(this.getFeatures(), {
            filter: this.options.filter,
            bounds: this.options.expandView ? this.getMap().getBounds().pad(this.options.expandView) : this.getMap().getBounds(),
        });
        this.state.featureCollection = featureCollection;
        return featureCollection;
    }

    panTo(latlng) {
        return this.getMap().panTo(latlng);
    }

    closePopup() {
        if (this._popupTimeout) {
            clearTimeout(this._popupTimeout);
        }

        if (this.popup) {
            this.getMap().removeLayer(this.popup);
            delete this.popup;
        }
    }

    show(id) {
        if (id) {
            if (this.state.hidden.has(String(id))) {
                // this.layer.addLayer(this.state.hidden.get(String(id)));
                this.state.hidden.delete(String(id));
                this.reload();
            }
        } else {
            this.getMap().addLayer(this.layer);
            this._show();
        }
    }

    hide(id) {
        if (id) {
            const layer = this.getLayer(String(id));
            if (layer && this.layer.hasLayer(layer)) {
                this.state.hidden.set(String(id), layer);
                // this.layer.removeLayer(layer);
                this.reload();
            }
        } else {
            this._clear();
            if (this.options.remove) {
                this.options.remove(this.toGeoJSON().features);
            }
        }
    }

    isHidden(id) {
        return this.state.hidden.has(String(id));
    }

    highlight(feature, options = {}) {
        // console.time "L.VectorLayer.GeoJSON -> highlight"
        if (!feature) return;
        const style = options.style || this._createStyle(feature);
        const layer = options.layer || this.getLayer(this._getFeatureId(feature));
        if (!layer) return;

        this._changeLayerStyle(feature, layer, {
            color: tinycolor(style.highlight).isValid() ? style.highlight : style.color,
            weight: (style.weight || 1) * 2.5,
            icon: style.highlight,
            force: options.force
        });

        if (options.fixed) {
            this.state.highlight.set(this._getFeatureId(feature), true);
        }
    }

    unhighlight(feature, options = {}) {
        // console.time "L.VectorLayer.GeoJSON -> unhighlight"
        if (_.isBoolean(feature) && feature === true) {
            return this._resetLayerStyle();
        }

        if (!feature) return;
        const style = options.style || this._createStyle(feature);
        const layer = options.layer || this.getLayer(this._getFeatureId(feature));
        // console.log('L.VectorLayer.GeoJSON -> unhighlight', layer, feature, options);
        if (!layer) return;

        if (options.fixed) {
            return;
        }

        this._changeLayerStyle(feature, layer, {
            color: style.color,
            weight: style.weight,
            icon: style.icon,
            force: options.force
        });

        this.state.highlight.delete(this._getFeatureId(feature));
        // console.timeEnd "L.VectorLayer.GeoJSON -> unhighlight"
    }

    samePosition() {
        return this._checkSamePosition();
    }

    _setInitialState() {
        this.state = {
            cachePages: [],
            features: new Map(),
            featureCollection: {},
            hidden: new Map(),
            highlight: new Map(),
            icons: new Map(),
            position: undefined,
            visible: false
        };
    }

    _init() {
        // _globalPointer is a string that points to a global function variable
        this.id = this.options.id ? this.options.id : `spatial_${Math.floor(Math.random() * 100000)}`;
        if (this.options.global) {
            window[this.id] = this;
        }

        this._setInitialState();
    }

    _addEventListener() {
        this._eventListenerAdded = true;
        this._addIdleListener();
        this._addZoomListener();
    }

    _show() {
        this.state.visible = true;
        this._addEventListener();
        if (this._checkLayerZoomVisibility()) {
            if (this.options.autoUpdate && this.options.autoUpdateInterval) {
                this._autoUpdateInterval = setInterval(this._makeRequest, this.options.autoUpdateInterval);
            }

            if (this.options.showAll) {
                this._gotAll = true;
            }

            return this._makeRequest();
        }
    }

    _clear() {
        this.getMap().off('dragend', this._idleListener);
        this.getMap().off('zoomend', this._zoomChangeListener);
        this._eventListenerAdded = false;
        if (this._autoUpdateInterval) {
            clearInterval(this._autoUpdateInterval);
        }
        this._hideVectors();
        this._gotAll = false;
        this.state.visible = false;
    }

    _getFeatureId(feature = {}) {
        if (this.options.uniqueField && feature.properties) {
            if (_.isArray(this.options.uniqueField)) {
                return _.map(this.options.uniqueField, f => feature.properties[f]).join('');
            } else {
                return String(feature.properties[this.options.uniqueField]);
            }
        } else {
            return String(_.indexOf([...this.state.features.values()], f => _.isEqual(f, feature)));
        }
    }

    _removeLayerById() {
        this.getMap().eachLayer(l => {
            if (String(l.id) === String(this.id)) {
                this.getMap().removeLayer(l);
            }
        });
    }

    // Hide the vectors in the layer.
    // This might get called if the layer is still on but out of scaleRange.
    _hideVectors() {
        if (this.getMap()) {
            this._removeLegend();
            this._removeTags();
            this._removeLayerById();
            this._removeClusterPolygon();

            if (this.layer && this.getMap().hasLayer(this.layer)) {
                this.getMap().removeLayer(this.layer);
            }
        }
    }

    _removeClusterPolygon() {
        if (this.clusterPolygon) {
            this.getMap().removeLayer(this.clusterPolygon);
            delete this.clusterPolygon;
        }

        if (this.options.popup && (this.options.popup.options || {}).autoclose) {
            this.closePopup();
        }
    }

    _removeLegend() {
        if (this._legend) {
            this._legend.remove();
        }
        delete this._legend;
    }

    _removeTags() {
        if (this._tags) {
            this._tags.remove();
            delete this._tags;
        }
    }

    // Show the vectors in the layer.
    // This might get called if the layer is on and came back into scaleRange.
    _showVectors() {
        try {
            this.reload();
        } catch (err) {
            console.log(err);
        }
    }

    _checkLayerZoomVisibility() {
        if (this.options.scaleRange) {
            let _scaleRange;
            if (_.isArray(this.options.scaleRange)) {
                _scaleRange = this.options.scaleRange;
            } else if (this.options.scaleRange instanceof ZoomLevel) {
                if (!this.options.scaleRange.options.map) {
                    this.options.scaleRange.options.map = this.getMap();
                }
                _scaleRange = this.options.scaleRange.get();
            } else if (_.isFunction(this.options.scaleRange)) {
                _scaleRange = this.options.scaleRange();
            } else {
                return false;
            }

            // Check current map scale and see if it's in this layer's ranges
            if (_.isArray(_scaleRange) && _.size(_scaleRange) === 2) {
                let localeFilteredCantApplySmallLocation = false;
                const localeFilteredByNeocom = environment.get('LOCALE.FILTERED');
                if (localeFilteredByNeocom) {
                    localeFilteredCantApplySmallLocation =
                        localeFilteredByNeocom.IdTipoLocalidade !== LocaleTypeEnum.Pais
                        || localeFilteredByNeocom.IdTipoLocalidade !== LocaleTypeEnum.Regiao;
                }
                const z = this.getMap().getZoom();
                const sr = _scaleRange;
                this.options.visibleAtScale = _.inRange(z, sr[0] - 1, sr[1] + 1) || localeFilteredCantApplySmallLocation;

                if (!this.options.visibleAtScale) {
                    notifyOutOfScale();
                }

                return this.options.visibleAtScale;
            }
        }

        return true;
    }

    // Add an event handler to detect a zoom change on the map
    _addZoomListener() {
        // "this" means something different inside the on method. Assign it to "me".
        this.getMap().on('zoomend', this._zoomChangeListener);
    }

    // Add an event handler to detect an idle (pan or zoom) on the map
    _addIdleListener() {
        // Whenever the map idles (pan or zoom) get the features in the current map extent
        this.getMap().on('dragend', this._idleListener);
    }

    _triggerChangeEvent(movement) {
        if (movement === 'zoomend' && this.options.zoomend) {
            this.options.zoomend();
        } else if (movement === 'dragend' && this.options.dragend) {
            this.options.dragend();
        }
    }

    // prevent reload of layers if its already in the same place
    _checkSamePosition(movement) {
        if (movement) {
            this._triggerChangeEvent(movement);
        }

        const bbox = this.getMap().getBounds();

        const inside = this.state.position && this.state.position.contains(bbox);

        if (!inside) {
            this._updatePositionState();
        }

        return inside;
    }

    // This gets fired when the map is panned
    _idleListener() {
        // console.time('L.VectorLayer.Core -> _idleListenerTemplate');
        // Do not redraw if set to be hidden
        if (!this.visible()) return;

        this._fireMovementEvent('dragend');
        // console.timeEnd('L.VectorLayer.Core -> _idleListenerTemplate');
    }

    // Get the current map zoom and check to see if the layer should still be visible
    _zoomChangeListener() {
        // console.time('L.VectorLayer.Core -> _zoomChangeListener');
        // Do not redraw if set to be hidden
        if (!this.visible() && this.state.features.size) return;

        if (this._fireMovementEvent('zoomend')) {
            // Check to see if we need to set any intervals for auto-updating layers
            if (this.options.autoUpdate && this.options.autoUpdateInterval) {
                this._autoUpdateInterval = setInterval(() => this._makeRequest(), this.options.autoUpdateInterval);
            }
        } else {
            this._hideVectors();

            if (_.isFunction(this.options.hide)) {
                this.options.hide();
            }
            // Check to see if we need clear any intervals for auto-updating layers
            if (this._autoUpdateInterval) {
                clearInterval(this._autoUpdateInterval);
            }
        }

        // console.timeEnd('L.VectorLayer.Core -> _zoomChangeListener');
    }

    // Set the Label content for the feature
    _setFeatureContent(type, feature, layer) {
        return new Promise((resolve, reject) => {
            try {
                if (_.isArray(feature) && type === 'popup') {
                    resolve(this.options[type].clusterTemplate(feature, layer));
                } else {
                    // Check to see if it's a string-based popup.template or function
                    if (this.options[type] && _.isString(this.options[type].template)) {
                        // Store the string-based popup.template
                        let popupContent = this.options[type].template;
                        // Loop through the properties and replace mustache-wrapped property names with actual values
                        _.forEach(feature.properties, p => {
                            const re = new RegExp(`{${p}}`, 'g');
                            popupContent = popupContent.replace(re, feature.properties[p]);
                        });

                        resolve(popupContent);
                    } else {
                        Promise.resolve(this.options[type] && this.options[type].template(feature.properties, layer)).then(
                            popupContent => resolve(popupContent)
                        );
                    }
                }
            } catch (err) { }
        });
    }

    _showLabel(feature, layer) {
        this._setFeatureContent('label', feature, layer).then(labelContent => {
            if (labelContent) {
                if (this.options.label.options) {
                    if (this.options.label.options.offset) {
                        layer.bindTooltip(labelContent, {
                            noHide: this.options.label.options.visible,
                            direction: this.options.label.options.direction,
                            offset: this.options.label.options.offset
                        });
                    } else {
                        layer.bindTooltip(labelContent, {
                            noHide: this.options.label.options.visible,
                            direction: this.options.label.options.direction
                        });
                    }
                    if (this.options.label.options.visible && layer.showLabel) {
                        layer.showLabel();
                    }
                } else {
                    layer.bindTooltip(labelContent);
                }
            }
        });
    }

    // Show the feature's (or layer's) Popup
    _showPopup(feature, layer) {
        // console.time "_showPopup"
        // If the layer already has an Popup created, close and delete it
        this.closePopup();

        // avoid openning popup when mouseover over various features quickly
        this._popupTimeout = setTimeout(() => {
            this._setFeatureContent('popup', feature, layer).then(popupContent => {
                // console.log('VectorLayer -> _showPopup', popupContent);
                this.popup = new L.Popup(this.options.popup.options);
                try {
                    if (!popupContent) {
                        return this.closePopup();
                    }

                    this.popup.setLatLng(_.isFunction(layer.getLatLng) ? layer.getLatLng() : getShapeCentroid(feature));

                    this.popup.setContent(_.isString(popupContent) ? popupContent : $(popupContent).html());
                    this.getMap().addLayer(this.popup);
                } catch (err) {
                    console.log(err);
                    this.closePopup();
                }
            });
        }, ((this.options.popup || {}).delay < 300 ? 300 : (this.options.popup || {}).delay) || 700);
    }

    // Optional click event
    _fireClickEvent(feature, layer) {
        if (this.options.click) {
            this.options.click(feature, layer);
        }
    }

    // Optional dblclick event
    _fireDoubleClickEvent(feature, layer) {
        if (this.options.dblclick) {
            this.options.dblclick(feature, layer);
        }
    }

    // Optional mouseover event
    _fireMouseoverEvent(feature, layer) {
        if (this.options.mouseover) {
            this.options.mouseover(feature, layer);
        }
    }

    // Optional mouseout event
    _fireMouseoutEvent(feature, layer) {
        if (this.options.mouseout) {
            this.options.mouseout(feature, layer);
        }
    }

    _getColorRange(feature) {
        let value = 0;
        if (feature) {
            if (typeof feature === 'object') {
                value = feature.properties[this.options.value];
            } else {
                value = feature;
            }
        }

        if (isValidNumber(value)) {
            value = this._getColorRangeValue(value);
        }

        let exclude = false;
        try {
            exclude = _.some(this.options.exclude, e => e === value);
        } catch (err) {
            // console.log(err);
        }

        if (!this._colorRange || exclude) {
            return 'transparent';
        }

        return this._colorRange(value);
    }

    _getColorRangeValue(value) {
        let v = _.toNumber(value);
        v = _.min([v, this._max]);
        v = _.max([v, this._min]);
        return v;
    }

    _createSymbologyScale(featureCollection = this.getCurrentGeoJSON()) {
        // return a function to set the color range based on data received
        const scale = createSymbologyScale(featureCollection.features, this.options);

        if (scale && scale.domain()) {
            // set the min and max domain to know the range variation of colors
            this._min = _.min(scale.domain());
            this._max = _.max(scale.domain());
        }

        return scale;
    }

    _getStyle(feature) {
        const featureProperties = feature.properties;
        const styleProperty = this.options.symbology.property;

        const copyStyle = s => _.cloneDeep(s) || {};

        let style = {};

        if (featureProperties.notPaint) {
            style = {
                fillOpacity: 0,
                opacity: 0,
                weight: 0,
                stroke: true,
                fillColor: 'transparent',
                color: 'transparent',
            };
            return style;
        }

        if (featureProperties.isComposedGroup) {
            return {
                fillOpacity: 0.7,
                opacity: 1,
                weight: 1,
                stroke: true,
                fillColor: '#BDBDBD',
                color: '#000000'
            };
        }

        const _styleType = {
            single: () => {
                style = copyStyle(this.options.symbology.style);
            },
            choropleth: () => {
                style = copyStyle(this.options.symbology.style);
                style.fillColor = this._getColorRange(feature);
            },
            polymorph: () => {
                if (this.options.symbology.values) {
                    const symbology = _.find(this.options.symbology.values, v => {
                        if (_.isString(featureProperties[styleProperty])) {
                            return featureProperties[styleProperty] === v.value;
                        }
                        return String(featureProperties[styleProperty]) === String(v.value);
                    });
                    if (symbology) {
                        style = copyStyle(symbology.style);

                        this._applyIconUrl(style, featureProperties);
                    }
                } else {
                    style = copyStyle(this.options.symbology.style);
                    this._applyIconUrl(style, featureProperties);
                }
            },
            uniq: () => {
                const symbology = _.find(this.options.symbology.values, v => {
                    if (_.isString(featureProperties[styleProperty])) {
                        return featureProperties[styleProperty] === v.value;
                    }
                    return String(featureProperties[styleProperty]) === String(v.value);
                });
                if (symbology) {
                    style = copyStyle(symbology.style);
                }
            },
            range: () => {
                const symbology = _.find(
                    this.options.symbology.ranges,
                    r =>
                        featureProperties[styleProperty] >= r.range[0] && featureProperties[styleProperty] <= r.range[1]
                );

                if (symbology) {
                    style = copyStyle(symbology.style);
                }
            }
        };

        // Is there a symbology set for this layer
        if ((this.options.symbology || {}).type) {
            _styleType[this.options.symbology.type]();
        }

        if (style.title) {
            _.forEach(featureProperties, function (v, k) {
                const re = new RegExp(`{${k}}`, 'g');
                style.title = _.replace(style.title, re, v);
            });
        }

        return style;
    }

    _applyIconUrl(style, featureProperties) {
        if (!style.icon)
            return;

        if (!!style.icon.fields)
            this._applyIconUrlUsingFields(style, featureProperties);

        if (!!style.icon.getUrl)
            style.icon.iconUrl = style.icon.getUrl(featureProperties);

        if (typeof style.icon.iconSize === 'function') {
            style.icon.iconSize = style.icon.iconSize(featureProperties);
        }
    }

    _applyIconUrlUsingFields(style, featureProperties) {
        _(style.icon.fields)
            .filter(p => !!featureProperties[p])
            .forEach(p => {
                const regex = new RegExp(`{${p}}`, 'g');
                const value = String(featureProperties[p]).toUnderscoreCase();
                style.icon.iconUrl = _.replace(style.icon.iconUrl, regex, value);
            });

        delete style.icon.fields;
    }

    _addCache(response, params = null) {
        if (!this.options.showAll) {
            delete params.bbox;
        }

        this.state.cachePages.push({
            bounds: this.getMap().getBounds(),
            response,
            params
        });

        if (this.state.cachePages.length === 20) {
            return this.state.cachePages.shift();
        }
    }

    _getCachedFeatures(requestParams, cache) {
        const cacheParams = cache.params;
        const { features } = cache.response;

        const paramsToArray = function (params) {
            if (_.isArray(params)) {
                return params;
            }
            return String(params)
                .replace(/, /g, ',')
                .split(',');
        };

        _.forEach(requestParams, (value, key) => {
            if (key === 'bbox') {
                return;
            }
            cacheParams[key] = paramsToArray(cacheParams[key]);
            requestParams[key] = paramsToArray(requestParams[key]);
        });

        if (_.some(requestParams, (value, key) => _.size(cacheParams[key]) < _.size(value))) {
            return [];
        }

        const bbox = getCurrentBounds(this.getMap().getBounds(), this.options);

        return _(features)
            .filter(f => turf.intersect(bbox, f))
            .filter(f =>
                _.every(requestParams, (v, k) => {
                    if (k === 'bbox') {
                        return true;
                    }
                    return _.some(v, p => p === f.properties[k]);
                })
            )
            .value();
    }

    _requestCache(params) {
        params = _.cloneDeep(params);
        if (this.state.cachePages.length === 0) {
            return null;
        }

        return _.reduce(
            this.state.cachePages,
            (res, cache) => {
                if (_.size(res)) {
                    return false;
                }
                // check if cache is inside de current bounds
                if (cache.bounds.contains(this.getMap().getBounds()) || this.options.showAll) {
                    if (cache.params) {
                        const features = this._getCachedFeatures(params, cache);
                        if (_.size(features)) {
                            return { features };
                        }
                    } else {
                        return cache.response;
                    }
                }
            },
            []
        );
    }

    _makeRequest() {
        let data;

        if (!this.options.remote || !this.options.remote.url) {
            return;
        }

        const _error = () => {
            notifyError({ status: 400 });
        };

        const _complete = geojson => {
            this._updatePositionState();
            this._processFeatures(geojson);
        };

        const _success = async res => {
            // Check for TopoJSON
            if (res[0].type === 'Topology') {
                const obj = res[0].objects && res[0].objects.output;

                if (!obj) {
                    res = [{ type: 'FeatureCollection', features: [] }];
                } else {
                    res = [topojson.feature(res[0], obj)];
                }
            }

            if (this.options.cache) {
                _(res)
                    .filter(r => !r.cache)
                    .forEach((r, i) => {
                        const params = data;
                        if (!this.options.showAll) {
                            params.bbox = data.bbox[i];
                        }
                        return this._addCache(r, params);
                    });
            }

            if (this.options.success) {
                try {
                    const sc = await this.options.success(res[0]);
                    _complete(sc);
                } catch (ex) {
                    _error(ex);
                }
            } else {
                const geojson = {};
                geojson.type = 'FeatureCollection';
                geojson.features = _.reduce(
                    res,
                    (pre, cur) => {
                        if (cur.features) {
                            return pre.concat(cur.features);
                        } else if (cur.properties) {
                            return pre.concat(cur);
                        }
                    },
                    []
                );
                _complete(geojson);
            }
        };

        const _request = params => {
            if (this.options.remote.params && this._getLocaleFiltered()) {
                this.options.remote.params.idLocaleFiltered = this._getLocaleFiltered();
            }

            if (this.options.remote.url) {
                return api.fetch({
                    type: this.options.remote.method || 'GET',
                    url: this.options.remote.url,
                    data: this.options.remote.method === 'POST' ? JSON.stringify(params) : params
                });
            }

            return new Promise((resolve, reject) => {
                resolve(this.toGeoJSON());
            });
        };

        if (this.options.remote.beforeSend) {
            this.options.remote.beforeSend();
        }

        data = this._queryBuilder(this.options.remote);

        let queue;
        if (this.options.showAll) {
            queue = [_request(data)];
        } else {
            queue = data.bbox.map(d => {
                const params = data;
                params.bbox = d;
                if (this.options.cache) {
                    const _cached = this._requestCache(data);
                    if (_cached) {
                        _cached.cache = true;
                        return new Promise(resolve => resolve(_cached));
                    }
                }
                return _request(params);
            });
        }

        return Promise.all(queue).then(_success, _error);
    }

    _updatePositionState() {
        this.state.position = this.getMap()
            .getBounds()
            .pad(this.options.expandView || 0.1);
    }

    _queryBuilder(request) {
        let data = {};
        if (this.options.remote.params && this._getLocaleFiltered()) {
            this.options.remote.params.idLocaleFiltered = this._getLocaleFiltered();
        }

        if (request.params) {
            data = _.cloneDeep(request.params);
        }

        if (!this.options.showAll) {
            data.bbox = this._getBounds(request.parallels);
        }

        return data;
    }

    _getBounds(parallels) {
        let bounds = this.getMap().getBounds();

        if (this.options.expandView > 0) {
            bounds = bounds.pad(this.options.expandView);
        }

        let bbox;

        if (parallels) {
            const sw = bounds.getSouthWest();
            const se = bounds.getSouthEast();
            const nw = bounds.getNorthWest();
            const ne = bounds.getNorthEast();
            const ct = bounds.getCenter();

            const b1 = L.bounds([[nw.lng, nw.lat], [ct.lng, ct.lat]]);
            const b2 = L.bounds([[ct.lng, ct.lat], [ne.lng, ne.lat]]);
            const b3 = L.bounds([[sw.lng, sw.lat], [ct.lng, ct.lat]]);
            const b4 = L.bounds([[ct.lng, ct.lat], [se.lng, se.lat]]);

            bbox = _.map([b1, b2, b3, b4], b => {
                const bl = b.getBottomLeft();
                const tr = b.getTopRight();
                return `LINESTRING(${tr.x} ${tr.y}, ${bl.x} ${bl.y})`;
            });
        } else {
            const sw = bounds.getSouthWest();
            const ne = bounds.getNorthEast();
            bbox = [`LINESTRING(${ne.lng} ${ne.lat}, ${sw.lng} ${sw.lat})`];
        }

        return bbox;
    }

    _getLocaleFiltered() {
        const locFiltered = environment.get('LOCALE.FILTERED');

        if (!locFiltered) {
            delete this.options.remote.params.idLocaleFiltered;
            return 0;
        }

        return locFiltered.id[0] || locFiltered.id;
    }
}

export default VectorLayer;



// WEBPACK FOOTER //
// ./src/scripts/layers/Leaflet/VectorLayer/VectorLayer.js